const TOLERANCE = 0.005;

export function coerceFlag(value) {
	return value === true || value === 1 || value === '1' || value === 'true';
}

export function locationCreditCardFeePercent(location) {
	if (!location || typeof location !== 'object' || Array.isArray(location)) {
		return 0;
	}
	const raw = location.credit_card_fee ?? location.CreditCardFee;
	const n = parseFloat(raw);
	return Number.isFinite(n) && n > 0 ? n : 0;
}

export function isAutoCcFeeEnabled(flags) {
	if (flags == null) {
		return false;
	}
	if (typeof flags !== 'object') {
		return coerceFlag(flags);
	}
	return coerceFlag(flags.auto_cc_fee) || coerceFlag(flags.rootState?.auto_cc_fee);
}

/** Default on when unset (matches account_customizations.apply_cc_fee_to_debit DEFAULT 1). */
export function isApplyCcFeeToDebitEnabled(flags) {
	if (flags == null) {
		return true;
	}
	if (typeof flags !== 'object') {
		return coerceFlag(flags);
	}
	const value = flags.apply_cc_fee_to_debit;
	if (value == null && flags.rootState && typeof flags.rootState === 'object') {
		const nested = flags.rootState.apply_cc_fee_to_debit;
		if (nested == null) {
			return true;
		}
		return coerceFlag(nested);
	}
	if (value == null) {
		return true;
	}
	return coerceFlag(value);
}

export function checkoutCcFeePercent(checkoutState, flags) {
	const fromCheckout = locationCreditCardFeePercent(checkoutState?.locations);
	if (fromCheckout > 0) {
		return fromCheckout;
	}
	return locationCreditCardFeePercent(flags?.location ?? flags?.posLocation ?? flags?.rootState?.location);
}

/** Debit tender is the POS Debit button — never loaded checkouts.is_debit (kiosk apply-fee). */
export function checkoutIsDebitTender(object) {
	return !!object?.showDebitCardButtons;
}

export function checkoutCardTenderSelected(object) {
	if (checkoutIsDebitTender(object)) {
		return true;
	}
	const cardAmount = parseFloat(object?.card_amount);
	return !!(
		object?.showCreditCardButtons ||
		object?.manual_card ||
		object?.sudo_card === 'card' ||
		(Number.isFinite(cardAmount) && cardAmount > TOLERANCE)
	);
}

export function computeCcFeeAmount(preFeeTotal, percent) {
	const amount = parseFloat(preFeeTotal);
	const pct = parseFloat(percent);
	if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(pct) || pct <= 0) {
		return 0;
	}
	return Math.ceil(((pct / 100) * amount) * 100) / 100;
}

/**
 * Apply the location CC fee for credit or debit processor tender.
 * Debit is skipped only when apply_cc_fee_to_debit is off.
 * Auto-apply: processor selected → fee on. Manual: only when card_percentage is on.
 */
export function checkoutShouldApplyCcFee(object, flags) {
	if (checkoutIsDebitTender(object) && !isApplyCcFeeToDebitEnabled(flags)) {
		return false;
	}
	if (checkoutCcFeePercent(object, flags) <= 0) {
		return false;
	}
	if (isAutoCcFeeEnabled(flags)) {
		return checkoutCardTenderSelected(object);
	}
	return coerceFlag(object?.card_percentage);
}

/** Never show Add CC Fee when auto-apply is on, or when debit is excluded from the fee. */
export function showManualCcFeeButton(object, flags) {
	if (isAutoCcFeeEnabled(flags)) {
		return false;
	}
	if (checkoutCcFeePercent(object, flags) <= 0) {
		return false;
	}
	if (checkoutIsDebitTender(object) && !isApplyCcFeeToDebitEnabled(flags)) {
		return false;
	}
	return true;
}

export function applyCheckoutCcFee(preFeeTotal, object, flags) {
	const percent = checkoutCcFeePercent(object, flags);
	if (!checkoutShouldApplyCcFee(object, flags)) {
		return {percent, card_fee: 0, amount: parseFloat(preFeeTotal) || 0};
	}
	const card_fee = computeCcFeeAmount(preFeeTotal, percent);
	return {percent, card_fee, amount: (parseFloat(preFeeTotal) || 0) + card_fee};
}

/**
 * Kiosk must not use "location has a CC fee" as a reason to add it.
 * Checkout persists a positive cc_fee_amount only when card tender should carry the fee.
 */
export function kioskShouldApplyCcFee(persistedCcFeeAmount) {
	const n = parseFloat(persistedCcFeeAmount);
	if (Number.isFinite(n)) {
		return n > TOLERANCE;
	}
	return coerceFlag(persistedCcFeeAmount);
}

export function applyKioskCcFee(preFeeTotal, persistedCcFeeAmount, locationPercent) {
	const amount = parseFloat(preFeeTotal) || 0;
	if (!kioskShouldApplyCcFee(persistedCcFeeAmount)) {
		return {cc_fee: 0, total: amount, percent: 0};
	}
	const percent = locationCreditCardFeePercent({credit_card_fee: locationPercent}) || parseFloat(locationPercent) || 0;
	const cc_fee = computeCcFeeAmount(amount, percent);
	return {cc_fee, total: amount + cc_fee, percent};
}

/** POS + kiosk mock: same pre-tax+tax total, shared apply decision. */
export function simulateCheckoutAndKiosk(preFeeTotal, checkoutState, flags) {
	const pos = applyCheckoutCcFee(preFeeTotal, checkoutState, flags);
	return {
		showAddCcFeeButton: showManualCcFeeButton(checkoutState, flags),
		posFee: pos.card_fee,
		posTotal: pos.amount,
		persistedCcFeeAmount: pos.card_fee > 0 ? pos.card_fee : 0,
		kiosk: applyKioskCcFee(preFeeTotal, pos.card_fee, checkoutCcFeePercent(checkoutState, flags)),
	};
}
